/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, FormEvent } from 'react';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { Button, Input } from 'antd';
import Project from '~src/assets/nav-icons/project.png';
import { isEmailValid, isPasswordValid } from '~src/utils/login';
import { useIntl } from 'react-intl';
import { LoginHelpInfoModal } from '~components/popups-modals/LoginHelpInfo.modal';

interface credentials {
  username: string;
  password: string;
}

interface LoginFormProps {
  onSubmit: (data: credentials) => void;
  isError: boolean;
  onForgotPasswordClick: () => void;
}

/** Login form */
const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isError,
  onForgotPasswordClick,
}) => {
  const style = useStyles(styleFn);
  const intl = useIntl();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isHelpLoginModalVisible, setHelpLoginModalVisible] = useState(false);

  const emailInvalid = formSubmitted && !isEmailValid(email);
  const passwordInvalid = formSubmitted && !isPasswordValid(password);
  const passwordTooShort =
    formSubmitted && password.length > 0 && password.length < 5;
  const passwordEmpty = formSubmitted && password.length === 0;

  const handleSubmit = (e?: FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    setFormSubmitted(true);

    const emailOK = isEmailValid(email);
    const passOK = isPasswordValid(password);

    if (!emailOK || !passOK) return;

    onSubmit({ password, username: email });
  };

  return (
    <form css={style.container} onSubmit={handleSubmit}>
      <h3 css={style.title}>
        <img src={Project} alt="" css={style.icon} />
        {intl.formatMessage({ id: 'c-web-project' })}
      </h3>

      <p css={style.label}>{intl.formatMessage({ id: 'email' })}:</p>
      <Input
        css={emailInvalid && style.inputInvalid}
        placeholder={intl.formatMessage({ id: 'email-placeholder' })}
        value={email}
        onChange={({ target: { value } }) => setEmail(value)}
      />
      {emailInvalid && (
        <p css={style.fieldError}>
          {intl.formatMessage(
            { id: 'login-invalid-email' },
            { defaultMessage: 'Invalid email address' }
          )}
        </p>
      )}

      <p css={style.label}>{intl.formatMessage({ id: 'password' })}:</p>
      <Input.Password
        css={passwordInvalid && style.inputInvalid}
        placeholder={intl.formatMessage({ id: 'password-placeholder' })}
        value={password}
        onChange={({ target: { value } }) => setPassword(value)}
      />
      {passwordInvalid && (
        <p css={style.fieldError}>
          {passwordEmpty &&
            intl.formatMessage(
              { id: 'login-empty-password' },
              { defaultMessage: 'Password is required' }
            )}

          {passwordTooShort &&
            intl.formatMessage(
              { id: 'login-password-too-short' },
              { defaultMessage: 'Password must be at least 5 characters' }
            )}
        </p>
      )}

      <Button
        css={style.button}
        htmlType="submit"
        onClick={() => handleSubmit()}>
        {intl.formatMessage({ id: 'login' })}
      </Button>

      {isError && (
        <p css={style.errorMessage}>
          {intl.formatMessage({ id: 'login-error' })}
        </p>
      )}

      <a
        css={style.link}
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onForgotPasswordClick();
        }}>
        {intl.formatMessage({ id: 'forgot-password' })}
      </a>

      <a
        css={style.link}
        href="#"
        onClick={(e) => {
          e.preventDefault();
          setHelpLoginModalVisible(true);
        }}>
        {intl.formatMessage({ id: 'help-to-log-in' })}
      </a>

      <a
        css={style.link}
        target="_blank"
        href="https://c-web.dk"
        rel="noreferrer">
        {intl.formatMessage({ id: 'go-to' })}
      </a>

      {!!window.localStorage.getItem('shares') && (
        <a
          css={style.backToInvitationsLink}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.location.pathname = '/invitations';
          }}>
          {intl.formatMessage({ id: 'back-to-invitations' })}
        </a>
      )}

      <LoginHelpInfoModal
        visible={isHelpLoginModalVisible}
        onClose={() => setHelpLoginModalVisible(false)}
        headerId="login_help_header"
      />
    </form>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    minWidth: '350px',
    height: '100vh',
    backgroundColor: theme.colors.white,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    marginTop: '20px',
  },
  link: {
    marginTop: '20px',
    color: 'black',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  backToInvitationsLink: {
    marginTop: '36px',
    color: 'black',
    alignSelf: 'center',
    cursor: 'pointer',
  },
  button: {
    textTransform: 'uppercase',
    backgroundColor: theme.colors.orange,
    border: 'none',
    color: theme.colors.white,
    marginTop: '20px',
    ':hover': {
      backgroundColor: theme.colors.darkOrange,
      color: theme.colors.white,
    },
    ':focus': {
      backgroundColor: theme.colors.darkOrange,
      color: theme.colors.white,
    },
  },
  icon: {
    width: '20px',
    marginRight: '10px',
  },
  inputInvalid: {
    border: `2px solid ${theme.colors.red}`,
  },
  fieldError: {
    marginTop: '6px',
    color: theme.colors.red,
    fontSize: '13px',
  },
  errorMessage: {
    marginTop: '10px',
    color: theme.colors.red,
    textAlign: 'center',
  },
});

export default LoginForm;
